import { FC, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { gql } from '@apollo/client';
import { StateFrom } from 'xstate';
import { useInterpret, useSelector as useXStateSelector } from '@xstate/react';
import { selectTemplateId } from 'modules/campaignAnalytics/selectors';
import { ExpandModes } from 'utils/contexts/expansion';
import { getUrlParams } from 'utils/urlParams';
import {
  selectNextBestActionEnabled,
  selectCampaignReactificationV1Enabled,
  getFeatureFlags,
} from 'core/userContext';
import { useQueryTemplateLocales } from 'modules/campaigns/editor/hooks/useTemplateLocales';
import { useQueryTemplateAndProjectLocales } from 'utils/hooks/locales/useQueryTemplateAndProjectLocales';
import { NBAContext } from 'modules/ai/nba/context/NBAContext';
import {
  createnbaContextStateMachineEffects,
  nbaContextStateMachine,
  NBAContextStates,
} from 'modules/ai/nba/context/NBAContextProvider.state';
import { useCampaignsDataQuery, useLazyTemplatesPreviewQuery } from 'modules/ai/hooks';
import { useNBAQuery } from 'modules/ai/nba/hooks';
import {
  createShouldSkipLocaleQuery,
  mergeCampaignData,
  parseTemplateLocaleData,
  setTemplateDataToRemove,
} from 'modules/ai/nba/utils/context';

import { CampaignData } from 'modules/ai/nba/types';

export const fetchCampaignsData = gql`
  query getCampaigns($ids: [Long!]) {
    campaigns(ids: $ids) {
      results {
        id
        messageMedium
        name
        templateId
        templateContentPreview
      }
    }
  }
`;

export const NBAContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const isNBAEnabled = useSelector(selectNextBestActionEnabled);
  const isCampaignsV1Enabled = useSelector(selectCampaignReactificationV1Enabled);
  const { LocaleImprovements } = useSelector(getFeatureFlags);
  const isLocalesImprovementsEnabled = Boolean(LocaleImprovements);
  const templateId = useSelector(selectTemplateId);
  const { search } = useLocation();
  const { campaignId } = getUrlParams(search);

  const [reengagementModalExpandMode, toggleIsModalShownLocal] = useState(ExpandModes.collapse);
  const toggleIsModalShown = useCallback(() => {
    toggleIsModalShownLocal((current) =>
      current === ExpandModes.collapse ? ExpandModes.expand : ExpandModes.collapse,
    );
  }, []);

  const { fetch: fetchNBAAI } = useNBAQuery(campaignId);

  const { fetch: fetchCampaigns } = useCampaignsDataQuery<CampaignData>(
    fetchCampaignsData,
    false,
    true,
    [],
  );

  const { mutateAsync: fetchTemplatePreviews } = useLazyTemplatesPreviewQuery();

  const { results: deprecatedTemplateLocalesData } = useQueryTemplateLocales({
    variables: { id: templateId },
    skip: createShouldSkipLocaleQuery(templateId)(isLocalesImprovementsEnabled),
  });
  const { results: templateLocalesData } = useQueryTemplateAndProjectLocales({
    variables: {
      templateId,
    },
    skip: createShouldSkipLocaleQuery(templateId)(!isLocalesImprovementsEnabled),
  });

  const parsedTemplateLocaleData = parseTemplateLocaleData(
    isLocalesImprovementsEnabled,
    deprecatedTemplateLocalesData,
    templateLocalesData,
  );

  const parsedTemplateLocaleDataEnhanced = setTemplateDataToRemove(parsedTemplateLocaleData);

  const service = useInterpret(
    nbaContextStateMachine,
    createnbaContextStateMachineEffects({
      fetchCampaigns,
      fetchNBAAI,
      fetchTemplatePreviews,
      isCampaignsV1Enabled,
      isNBAEnabled,
    }),
  );

  useEffect(() => {
    service.send(NBAContextStates.queryNBAAI);
  }, []);

  const isLoading = useXStateSelector(service, (state: StateFrom<typeof nbaContextStateMachine>) =>
    [
      NBAContextStates.queryNBAAI,
      NBAContextStates.querySupportCampaigns,
      NBAContextStates.queryTemplatePreview,
    ].some(state.matches),
  );
  const isError = useXStateSelector(service, (state: StateFrom<typeof nbaContextStateMachine>) =>
    state.matches(NBAContextStates.error),
  );
  const displayNBA = useXStateSelector(service, (state: StateFrom<typeof nbaContextStateMachine>) =>
    state.matches(NBAContextStates.ready),
  );
  const stateNBAData = useXStateSelector(
    service,
    (state: StateFrom<typeof nbaContextStateMachine>) => state.context.nbaData,
  );
  const stateMergedCampaignsData = useXStateSelector(
    service,
    (state: StateFrom<typeof nbaContextStateMachine>) =>
      mergeCampaignData(state.context.nbaData, state.context.campaignsData),
  );
  const stateTemplatesData = useXStateSelector(
    service,
    (state: StateFrom<typeof nbaContextStateMachine>) => state.context.templatesData,
  );
  return (
    <NBAContext.Provider
      value={{
        isLoading,
        isError,
        displayNBA,
        nbaData: stateNBAData,
        nbaCampaignsData: stateMergedCampaignsData,
        templatesData: stateTemplatesData,
        reengagementModalExpandMode,
        toggleIsModalShown,
        templateLocaleData: parsedTemplateLocaleDataEnhanced,
      }}
    >
      {children}
    </NBAContext.Provider>
  );
};
